import { useEffect, useMemo } from 'react';

import { isAxiosError } from 'axios';

import { useQuery } from '@tanstack/react-query';

import { API } from '@/shared/api';
import { USER_DATA_QUERY_KEY } from '@/shared/constants/queryKeys';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { Products } from '@/shared/types/Billing';
import { User } from '@/shared/types/User';

import { getAvailableProducts } from '../lib/getAvailableProducts';

const QUERY_KEY = 'products';

export const getProductsQueryKey = (userId?: string) => [QUERY_KEY, userId ?? 'anonymous'] as const;

const getProducts = async () => {
  const response = await API.getPlans();

  const sortedPlans = response.plan.sort((a, b) => a.metadata.position - b.metadata.position);

  return {
    boosters: response.boosters,
    credit: response.credit,
    plan: sortedPlans,
    pricing_experiment: response.pricing_experiment,
  };
};

type UseProductsOptions = {
  anonymous?: boolean;
  enableFallback?: boolean;
  showErrors?: boolean;
};

export const useProducts = (enabled = false, options: UseProductsOptions = {}) => {
  const { anonymous = false, enableFallback = false, showErrors = true } = options;
  const { data: user } = useQuery<User>({ enabled: false, queryKey: USER_DATA_QUERY_KEY });
  const userId = user?.id;
  const queryEnabled = enabled && (anonymous || Boolean(userId));

  const { data, error, isLoading, isSuccess } = useQuery<Products>({
    enabled: queryEnabled,
    queryFn: getProducts,
    queryKey: getProductsQueryKey(userId),
    refetchOnMount: false,
    retry: (failureCount, retryError) => {
      if (failureCount >= 2) return false;

      const status = isAxiosError(retryError) ? retryError.response?.status : undefined;
      if (status === 429) return failureCount < 1;

      return true;
    },
    retryDelay: (attemptIndex) => Math.min(5000 * 2 ** attemptIndex, 30_000),
    staleTime: Infinity,
  });

  useEffect(() => {
    if (error && showErrors) {
      // Don't show error alerts for rate limiting
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status !== 429) {
        showErrorAlert(error);
      }
    }
  }, [error, showErrors]);

  const fallbackData = useMemo<Products | undefined>(
    () => (enableFallback ? { boosters: [], credit: [], plan: [] } : undefined),
    [enableFallback],
  );

  const products = data ?? (queryEnabled ? fallbackData : undefined);
  const availableProducts = useMemo(() => getAvailableProducts(products, user), [products, user]);

  return {
    availableProducts,
    error,
    isLoading,
    isSuccess,
    products,
  };
};
